import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNetworkStatus } from "@/contexts/NetworkStatusContext";
import { Wifi, WifiOff, Database } from "lucide-react";
import { getDatabaseVersion } from "@/lib/db/indexedDB";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dbVersion, setDbVersion] = useState<number | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useAuth();
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    // Set the database version on component mount
    setDbVersion(getDatabaseVersion());
  }, []);

  // Format the Unix timestamp to a readable date
  const formatDbVersion = (timestamp: number | null) => {
    if (!timestamp) return "Unknown";
    
    const date = new Date(timestamp * 1000);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Bypass authentication and redirect directly to dashboard/posv2
    console.log("Login validation bypassed, redirecting to POSv2");
    toast({
      title: "Development mode",
      description: "Authentication bypassed for development purposes",
    });
    navigate("/dashboard");
    
    // Original authentication code (commented out for now)
    /*
    try {
      console.log("Login form submitted with:", { email, password });
      const success = await login(email, password);
      
      if (success) {
        console.log("Login successful, navigating to dashboard");
        toast({
          title: "Login successful",
          description: "Welcome back!",
        });
        navigate("/dashboard");
      } else {
        console.log("Login failed");
        toast({
          variant: "destructive",
          title: "Login failed",
          description: "Please check your credentials and try again.",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        variant: "destructive",
        title: "Login failed",
        description: "An error occurred during login.",
      });
    }
    */
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-[400px]">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Login</CardTitle>
            {isOnline ? (
              <div className="flex items-center text-green-600">
                <Wifi className="h-4 w-4 mr-1" />
                <span className="text-xs">Online</span>
              </div>
            ) : (
              <div className="flex items-center text-orange-600">
                <WifiOff className="h-4 w-4 mr-1" />
                <span className="text-xs">Offline</span>
              </div>
            )}
          </div>
          <CardDescription>Enter your credentials to access your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-[#8CC84B] hover:bg-[#4D9E41]">
              Sign In
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <div className="text-center w-full">
            <p className="text-sm text-muted-foreground mb-2">Don't have an account?</p>
            <Button asChild variant="outline" className="border-[#8CC84B] text-[#4D9E41]">
              <Link to="/register">Create Account</Link>
            </Button>
          </div>
          <div className="flex items-center justify-center text-xs text-muted-foreground gap-1 w-full">
            <Database className="h-3 w-3" />
            <span>Database Version: {dbVersion ? dbVersion : "Loading..."}</span>
          </div>
          <div className="text-xs text-muted-foreground text-center">
            Last Updated: {formatDbVersion(dbVersion)}
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default LoginPage;
